import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import AccountRoles from '../../account/enums/AccountRoles';
import ConfirmationDialog from '../../common/component/form/ConfirmationDialog';
import Table from '../../common/component/table/Table';
import {sortCaseInsensitive} from '../../common/util/SortUtil';
import {clearMessages} from '../../message/action/messageActions';
import teamApi from '../../team/api/teamApi';
import {noEntryOptional} from '../../training/util/TrainingUtilities';
import TeamDialog from './TeamDialog';

function TeamList({
                      clubId
                  }) {

    const role = useSelector(state => state.account.role);
    const [t] = useTranslation();
    const dispatch = useDispatch();

    const [teams, setTeams] = useState([]);

    const [teamIdForDeletion, setTeamIdForDeletion] = useState(undefined);
    const [showDeletionConfirmation, setShowDeletionConfirmation] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState(t('Team.ADD'));

    useEffect(() => {
        loadTeams();
    }, [clubId]);

    useEffect(() => {
        //if we open the dialog we clear old error messages
        if (showDeletionConfirmation) {
            dispatch(clearMessages());
        }
    }, [showDeletionConfirmation]);

    const loadTeams = () => {
        teamApi.getTeamsOfClub(clubId).then((response) => {
            if (response.data.success) {
                setTeams(response.data.result);
            }
        });
    };

    const deleteTeam = () => {
        teamApi.delete(teamIdForDeletion).then((response) => {
            if (response.data.success) {
                loadTeams();
                setTeamIdForDeletion(null);
            }
        });
    };

    const columns = [
        {
            Header: t('Team.NAME'),
            accessor: 'name',
            Cell: props => {
                const text = `${props.cell.row.original.name}`;
                return noEntryOptional(text);
            },
            sortType: (a, b) => sortCaseInsensitive(a.original.name, b.original.name)
        }, {
            Header: '',
            accessor: 'id',
            Cell: props => {
                return (
                    (role === AccountRoles.ADMINISTRATOR ||
                        role === AccountRoles.CLUB_MANAGER ||
                        role === AccountRoles.TRAINER) &&
                    <>
                        <button
                            className="btn btn-transparent-secondary btn-icon"
                            onClick={() => {
                                setTeamIdForDeletion(props.cell.value);
                                setShowDeletionConfirmation(true);
                            }}
                            title={t('Button.DELETE')}
                        >
                            {<FontAwesomeIcon icon="trash"/>}
                        </button>
                        <button
                            className="btn btn-transparent-secondary btn-icon"
                            onClick={() => {
                                setSelectedTeam(teams.find(team => team.id === props.cell.value));
                                setShowEditDialog(true);
                                setDialogTitle(t('Team.UPDATE'));
                            }}
                            title={t('Button.EDIT')}
                        >
                            <FontAwesomeIcon icon="pencil-alt"/>
                        </button>
                    </>
                );
            }
        }];

    return (
        <>
            <div className="align-right">
                <h3 className="list-heading">
                    {t('Team.TITLE')}
                </h3>
                {
                    (role === AccountRoles.ADMINISTRATOR || role === AccountRoles.CLUB_MANAGER || role === AccountRoles.TRAINER) &&
                    <button
                        className="btn btn-primary ml-3"
                        onClick={() => {
                            setShowEditDialog(true);
                            setDialogTitle(t('Team.ADD'));
                        }}
                    >
                        <FontAwesomeIcon icon="plus" className="mr-2"/>
                        {t('Team.ADD')}
                    </button>
                }
            </div>

            <Table
                columns={columns}
                data={teams}
                sortable={true}
                sortBy={'name'}
                initialSort={[{
                    id: 'name',
                    desc: false
                }]}
            />

            <ConfirmationDialog
                title={t('Dialog.DELETE_TEAM')}
                open={showDeletionConfirmation}
                description={t('Dialog.DELETE_TEAM_QUESTION')}
                accept={() => {
                    deleteTeam();
                    setTeamIdForDeletion(undefined);
                    setShowDeletionConfirmation(false);
                }}
                cancel={() => {
                    setTeamIdForDeletion(undefined);
                    setShowDeletionConfirmation(false);
                }}
                acceptLabel={t('Button.DELETE')}
            />
            <TeamDialog
                clubId={clubId}
                title={dialogTitle}
                open={showEditDialog}
                onSave={() => {
                    setShowEditDialog(false);
                    setSelectedTeam(null);
                    loadTeams();
                }}
                onCancel={() => {
                    setShowEditDialog(false);
                    setSelectedTeam(null);
                }}
                team={selectedTeam}
            />
        </>
    );
}

export default TeamList;
